import { useIsMobile } from '@/hooks/use-is-mobile';
import { styleMaxLines } from '@/libs/material/sx';
import { useAudio } from '@rhg/hooks/use-audio';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Fade,
    Stack,
    SxProps,
    Theme,
    Typography,
    useTheme,
} from '@rhg/libs/material';
import { useIntl } from 'react-intl';
import { AudioPlayerCredit, AudioPlayerCredits } from '../credits';
import { AudioPlayerControls } from '../types';

const styleNarratorSection = {
    mb: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};
const styleTitle = styleMaxLines(2);
const styleDescription = styleMaxLines(4);
const styleScrollableDescriptionContainer = {
    overflowY: 'auto',
    maxHeight: 96,
    mb: 8,
    zIndex: 2,
};

interface AudioPlayerContentProps {
    title?: string;
    description?: string;
    narrator?: AudioPlayerCredit;
    author?: AudioPlayerCredit;
    controls?: AudioPlayerControls;
    sx?: SxProps<Theme>;
    areControlsHidden?: boolean;
    shouldHideExtras?: boolean;
}

export function AudioPlayerContent({
    title,
    description,
    narrator,
    author,
    controls = AudioPlayerControls.Regular,
    sx,
    areControlsHidden,
    shouldHideExtras,
}: AudioPlayerContentProps) {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMobile = useIsMobile();
    const {
        controls: { isShowingSubtitles },
    } = useAudio();

    return (
        <Fade in={!areControlsHidden} mountOnEnter unmountOnExit>
            <Stack direction="column" alignItems="center" sx={sx}>
                {title && (
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h1"
                        color="text.primary"
                        textAlign="center"
                        mb={description ? 2 : theme.ds.spacingUnits.SECTION}
                        sx={styleTitle}
                    >
                        {title}
                    </Typography>
                )}
                {description && !shouldHideExtras && (
                    <Box
                        {...(isShowingSubtitles
                            ? {
                                  sx: styleScrollableDescriptionContainer,
                                  tabIndex: 0,
                                  role: 'note',
                                  'aria-label': formatMessage(
                                      commonMessages['common.audio_description']
                                  ),
                              }
                            : undefined)}
                    >
                        <Typography
                            variant="body1"
                            color="text.body"
                            textAlign="center"
                            sx={
                                isShowingSubtitles
                                    ? undefined
                                    : {
                                          ...styleDescription,
                                          mb: (theme: Theme) =>
                                              theme.ds.spacingUnits.SECTION,
                                      }
                            }
                        >
                            {description}
                        </Typography>
                    </Box>
                )}
                {!shouldHideExtras && (
                    <AudioPlayerCredits
                        narrator={narrator}
                        author={author}
                        controls={controls}
                        sx={styleNarratorSection}
                    />
                )}
            </Stack>
        </Fade>
    );
}
